import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { formatChartTitle } from "../../utils";

interface GaugeChartProps {
  /** Pass null when the cohort has no readings, so the gauge shows "No data". */
  value: number | null;
  min?: number;
  max?: number;
  baseline?: { min: number; max: number };
  unit?: string;
  height?: number | string;
  title?: string;
  useNeutralColors?: boolean;
}

const SEGMENTS = [
  { start: 0, end: 15 },
  { start: 15, end: 35 },
  { start: 35, end: 65 },
  { start: 65, end: 85 },
  { start: 85, end: 100 },
];

/**
 * Segment boundaries expressed against the baseline range rather than the axis.
 *
 * The middle band is the baseline itself; the two inner bands extend half a
 * baseline width either side, and the outer bands run to the axis ends. Deriving
 * these from the axis instead (BT-249) produced labels like "Near baseline
 * (42-78 ms)" directly above a caption reading "Baseline 45-60ms".
 */
const OUTER_BAND_MULTIPLIER = 0.5;

const START_ANGLE = -90;
const END_ANGLE = 90;

const SEGMENT_LABELS = [
  "Well below baseline",
  "Below baseline",
  "Within baseline",
  "Above baseline",
  "Well above baseline",
];

/** Trim trailing ".0" so whole numbers read as "45" rather than "45.0". */
const formatBound = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

export const GaugeChart: React.FC<GaugeChartProps> = ({
  value,
  min = 0,
  max = 100,
  baseline,
  unit,
  height = "100%",
  title,
  useNeutralColors = false,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);
  const formattedTitle = formatChartTitle(title);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const hasValue = value !== null && Number.isFinite(value);
  const chartHeight = typeof height === "number" ? height : 300;

  // Boundaries in real units. With a baseline the middle band IS the baseline
  // range, so the tooltip and the "Baseline x-y" caption below always agree.
  // Without one, fall back to the original fixed proportions of the axis.
  const bandBounds: number[] = (() => {
    if (baseline) {
      const width = baseline.max - baseline.min;
      const step = width * OUTER_BAND_MULTIPLIER;
      return [
        min,
        Math.max(min, baseline.min - step),
        baseline.min,
        baseline.max,
        Math.min(max, baseline.max + step),
        max,
      ];
    }
    const range = max - min;
    return [min, ...SEGMENTS.map((s) => min + (s.end / 100) * range)];
  })();

  // The arc keeps its fixed five-segment geometry, so a value is placed by
  // interpolating within whichever band contains it. A linear (value - min) /
  // (max - min) sweep would drift out of the segment its own tooltip describes
  // once the bands stop being equal fifths.
  const positionInArc = (raw: number) => {
    if (raw <= bandBounds[0]) return 0;
    for (let i = 0; i < SEGMENTS.length; i += 1) {
      const lower = bandBounds[i];
      const upper = bandBounds[i + 1];
      if (raw > upper) continue;
      const span = upper - lower;
      const withinBand = span > 0 ? (raw - lower) / span : 0;
      const segment = SEGMENTS[i];
      return (segment.start + withinBand * (segment.end - segment.start)) / 100;
    }
    return 1;
  };

  const padding = 20;
  const availableWidth = width ? Math.max(0, width - padding) : chartHeight;
  const outerRadius = Math.min(availableWidth / 2, chartHeight);
  const innerRadius = outerRadius * 0.85;
  const arcCenterRadius = (outerRadius + innerRadius) / 2;
  const indicatorSize = ((outerRadius - innerRadius) / 2) * 0.7;

  const getIndicatorColor = (pct: number) => {
    if (useNeutralColors) {
      if (pct <= 50) return theme.palette.primary.dark;
      return theme.palette.primary.light;
    }
    if (pct <= 15) return theme.palette.error.dark;
    if (pct <= 35) return theme.palette.warning.dark;
    if (pct <= 65) return theme.palette.success.dark;
    if (pct <= 85) return theme.palette.warning.main;
    return theme.palette.error.dark;
  };

  const normalizedValue = hasValue ? positionInArc(value as number) : 0;
  const totalAngle = END_ANGLE - START_ANGLE;
  const indicatorAngle = START_ANGLE + normalizedValue * totalAngle;
  const indicatorColor = getIndicatorColor(normalizedValue * 100);

  // `value` here is the arc's angular width, which is why the raw numbers must
  // never reach the tooltip — hovering used to report 15/20/30/20/15, the widths
  // of the five arcs, rather than anything about the metric (BT-249).
  const pieData = SEGMENTS.map((segment, index) => {
    const lower = bandBounds[index];
    const upper = bandBounds[index + 1];
    const unitSuffix = unit ? ` ${unit}` : "";
    const rangeLabel = `${formatBound(lower)}-${formatBound(upper)}${unitSuffix}`;
    return {
      id: index,
      value: segment.end - segment.start,
      label: baseline
        ? `${SEGMENT_LABELS[index]} (${rangeLabel})`
        : rangeLabel,
    };
  });

  const segmentColors = useNeutralColors
    ? [
        theme.palette.primary.dark,
        theme.palette.primary.main,
        theme.palette.primary.main,
        theme.palette.primary.main,
        theme.palette.primary.light,
      ]
    : [
        theme.palette.error.main,
        theme.palette.warning.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
      ];

  const chartWidth = outerRadius * 2;
  const svgHeight = outerRadius;
  const centerY = `calc(50% + ${svgHeight / 2}px)`;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: chartHeight,
          position: "relative",
        }}
      >
        {width > 0 && (
          <>
            <Box
              sx={{
                position: "absolute",
                top: `calc(50% - ${svgHeight / 2}px)`,
                left: "50%",
                transform: "translateX(-50%)",
                width: chartWidth,
                height: svgHeight,
                overflow: "hidden",
                zIndex: 1,
              }}
            >
              <PieChart
                series={[
                  {
                    data: pieData,
                    innerRadius,
                    outerRadius,
                    startAngle: START_ANGLE,
                    endAngle: END_ANGLE,
                    paddingAngle: 2,
                    cornerRadius: 8,
                    cx: outerRadius,
                    cy: svgHeight,
                    // Suppress the arc width, leaving the tooltip to show only
                    // the band's own label.
                    valueFormatter: () => null,
                  },
                ]}
                colors={segmentColors}
                width={chartWidth}
                height={svgHeight}
                hideLegend
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              />
            </Box>
            {/* No needle without a reading — parked at the scale minimum it
                reads as a genuine "worst possible" measurement. */}
            {hasValue && (
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: centerY,
                  width: indicatorSize * 2.5,
                  height: indicatorSize * 2.5,
                  transform: `translate(-50%, -50%) rotate(${indicatorAngle}deg) translateY(-${
                    arcCenterRadius - indicatorSize * 0.5
                  }px)`,
                  transformOrigin: "center center",
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              >
                <Box
                  sx={{
                    width: indicatorSize * 1.5,
                    height: indicatorSize * 1.5,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: "0 3px 8px rgba(0, 0, 0, 0.3)",
                    outline: `${10}px solid ${indicatorColor}`,
                  }}
                />
              </Box>
            )}
          </>
        )}

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            textAlign: "center",
            pt: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "center",
            }}
          >
            <Typography
              variant={hasValue ? "h4" : "body1"}
              fontWeight={700}
              color={hasValue ? "text.primary" : "text.secondary"}
              sx={{ lineHeight: 1 }}
            >
              {hasValue ? value : "No data"}
            </Typography>
            {hasValue && unit && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 0.5 }}
              >
                {unit}
              </Typography>
            )}
          </Box>
          {formattedTitle && (
            <Typography variant="body2" color="text.secondary">
              {formattedTitle}
            </Typography>
          )}
          {baseline && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.5, opacity: 0.7 }}
            >
              Baseline {formatBound(baseline.min)}-{formatBound(baseline.max)}
              {unit ? ` ${unit}` : ""}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
